import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useEffect } from "react";
import { getData } from "../../Services/getData";
import { updateData } from "../../Services/updateData";
import { endpoints } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { Save } from "@mui/icons-material";
import styles from "../../Styles/settings.module.css";
import Config from "../../Utils/Config";
import { WordPressSettingsValidationSchema } from "../../Utils/Models";
import { AxiosResponse } from "axios";
import { GreenSwitch } from "../../Utils/Utils";

export default function WordPressSettings() {
  const websocketContext = useContext(WebSocketContext);

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: yupResolver(WordPressSettingsValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    criteriaMode: "firstError",
  });

  const isActive = watch("value.isActive");

  useEffect(() => {
    getData(endpoints.setting.wordPress).then((res: AxiosResponse) => {
      if (res?.data) {
        reset(res.data);
      }
    });
  }, [reset]);

  const onSubmit = (data: any) => {
    const url = `${endpoints.setting.updateWordPress}${data?._id}`;
    updateData(url, data).then((res: AxiosResponse) => {
      if (res?.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        reset(res.data);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.settingsForm}>
      {/* Active Toggle - Outside the grid alignment */}
      <Grid container spacing={Config.standardGridSpacing} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {Vocabulary.wordPressIsActive}
            <Controller
              name="value.isActive"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <GreenSwitch checked={value} onChange={onChange} />
              )}
            />
          </Typography>
        </Grid>
      </Grid>

      {/* 2x2 Grid for fields */}
      {isActive && (
        <Grid container spacing={Config.standardGridSpacing}>
          {/* Row 1: API URL and Sync Frequency */}
          <Grid item xs={12} md={6}>
            <Controller
              name="value.apiUrl"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.wordPressApiUrl}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder="https://example.com"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="value.syncFrequency"
              control={control}
              defaultValue="daily"
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel>{Vocabulary.wordPressSyncFrequency}</InputLabel>
                  <Select {...field} label={Vocabulary.wordPressSyncFrequency}>
                    <MenuItem value="manual">
                      {Vocabulary.wordPressSyncManual}
                    </MenuItem>
                    <MenuItem value="hourly">
                      {Vocabulary.wordPressSyncHourly}
                    </MenuItem>
                    <MenuItem value="daily">
                      {Vocabulary.wordPressSyncDaily}
                    </MenuItem>
                    <MenuItem value="weekly">
                      {Vocabulary.wordPressSyncWeekly}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Row 2: API Key and API Secret */}
          <Grid item xs={12} md={6}>
            <Controller
              name="value.apiKey"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.wordPressApiKey}
                  type="password"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="value.apiSecret"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.wordPressApiSecret}
                  type="password"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12} md={3}>
            <Button
              type="submit"
              fullWidth
              startIcon={<Save />}
              variant="contained"
            >
              {Vocabulary.save}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Save Button when inactive */}
      {!isActive && (
        <Grid container spacing={Config.standardGridSpacing}>
          <Grid item xs={12} md={3}>
            <Button
              type="submit"
              fullWidth
              startIcon={<Save />}
              variant="contained"
            >
              {Vocabulary.save}
            </Button>
          </Grid>
        </Grid>
      )}
    </form>
  );
}
